/*
  Split test `atc-upsell-popup`, control instrumentation.

  The seven events have to fire identically in both variants or the numbers do
  not compare. Variant-a publishes its own from its controller. Control cannot:
  its logic lives in product-addons.js and its minified twin, and any diff to
  those files is a diff to the thing being measured.

  So control is instrumented from the outside, by watching the DOM it already
  produces. No control file is touched and no control behaviour changes; this
  script only reads and publishes.
*/
(function () {
  'use strict';

  var TEST_ID = 'atc-upsell-popup';
  var MOUNT_ID = 'addons-drawer-mount';
  var DRAWER = 'product-addons-drawer';

  /*
    Resolving the assignment.

    window.splt.getVariant() compares its argument against splitTestId only, so
    it returns null for the handle even though the whole markup contract is
    keyed on the handle through data-split-id. Reading the assignment list
    directly matches on either, so this keeps working whichever the app settles
    on. Filed against the app as its own bug.
  */
  function variant() {
    try {
      var list = (window.splt && window.splt.assignments) || [];
      for (var i = 0; i < list.length; i += 1) {
        var entry = list[i];
        if (!entry || !entry.variant) continue;
        if (entry.splitTestHandle === TEST_ID || entry.splitTestId === TEST_ID) return entry.variant.handle;
      }
      if (window.splt && typeof window.splt.getVariant === 'function') return window.splt.getVariant(TEST_ID);
      return null;
    } catch (err) {
      return null;
    }
  }

  /*
    Which arm to publish this session as, or null to publish nothing.

    Variant-a publishes from its own controller, so publishing here as well
    would double every count for those visitors.

    An unassigned visitor is not a control visitor. They see control's drawer,
    which is correct and unchanged, but they were never randomised into
    anything: they are whoever hit an assignment outage, a blocked request or
    a slow load, which is a different population from the one the test picked.
    Publishing them as control puts non-randomised traffic in control's
    denominator, and hides the outage that produced them. The bootstrap already
    reports a 401 from the assignment proxy as splt_proxy_status, and this store
    has had one.

    Kept separate rather than dropped, because merging is one way. Control and
    unassigned can always be summed at analysis time; they can never be split
    apart afterwards.
  */
  function armFor() {
    var assigned = variant();
    if (assigned === 'variant-a') return null;
    return assigned === 'control' ? 'control' : 'unassigned';
  }

  function report(stage, err, extra) {
    if (typeof window.wbUpsellLog === 'function') return window.wbUpsellLog(stage, err, extra);
    try {
      if (window.slog) {
        window.slog('custom', {
          message: 'upsell_popup: ' + stage,
          extra: { kind: 'upsell_popup', stage: stage, error: String((err && err.message) || err || '') },
        });
      }
    } catch (e) {
      /* reporting must never break the drawer */
    }
  }

  function publish(name, payload) {
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(name, payload || {});
      }
    } catch (err) {
      /* analytics must never break the drawer */
    }
  }

  var state = {
    open: false,
    added: false,
    completed: false,
    attached: false,
    steps: {},
    handle: '',
    previous: { closure: null, groups: {} },
  };

  function fire(name, extra) {
    /*
      Re-checked on every event, not once at start.

      Assignments resolve after this file runs, so a variant-a session looked
      like control at bind time and then published control events alongside
      variant-a's own. Both arms counted the same interaction, which is worse
      than no data: it moves the numbers the test is judged on.
    */
    var arm = armFor();
    if (!arm) return;

    // The cart's add-ons drawer is control's for everyone, whichever arm the
    // visitor is in, so its activity is not this test's and must not be counted
    // into either side. Control marks that flow with the parent line's key.
    if (state.attached) return;

    var payload = { test: TEST_ID, variant: arm, handle: state.handle };
    if (extra) {
      Object.keys(extra).forEach(function (key) {
        payload[key] = extra[key];
      });
    }
    publish(name, payload);
  }

  function isVisible(el) {
    if (!el) return false;
    return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
  }

  function stepEventName(stepEl) {
    var step = stepEl.getAttribute('data-drawer-step');
    if (step === '1') return 'upsell_step1_viewed';
    if (step === '2') return 'upsell_step2_viewed';
    return '';
  }

  function scanSteps(drawer) {
    drawer.querySelectorAll('[data-drawer-step]').forEach(function (stepEl) {
      var name = stepEventName(stepEl);
      if (!name || state.steps[name]) return;
      if (!isVisible(stepEl)) return;
      state.steps[name] = true;
      fire(name);
    });
  }

  function onDrawerVisible(drawer) {
    if (state.open) return;
    state.open = true;
    state.added = false;
    state.completed = false;
    state.steps = {};
    state.handle = drawer.getAttribute('data-product-handle') || '';
    /*
      The property, not only the attribute.

      The cart drawer's "want an add-on?" button opens this same drawer for a
      line already in the cart, and that flow belongs to neither arm. Control
      marks it with attachToParentKey, and mirrors that into the attribute,
      but only on the path where the drawer was already mounted for that
      product. When the drawer has to be fetched first, connectedCallback
      consumes the pending context before the attribute is written, so the
      attribute is missing on exactly the cart opens that happen most: any
      page that is not that product's own.

      Those opens were published as control's, with no add to cart behind
      them. Over the test so far control recorded 7479 opens and 3048
      dismissals against variant-a's 5439 and 1251; the extra 2000 opens and
      1800 dismissals are the size of this leak. The attribute is still read
      for a drawer built before the property existed.
    */
    state.attached = !!(drawer.attachToParentKey || drawer.getAttribute('data-attach-parent-key'));
    state.previous = { closure: null, groups: {} };
    fire('upsell_popup_opened');
    scanSteps(drawer);
    // What is checked as the drawer opens, so the first change in a group can
    // say what it replaced. The free buckle default is the usual occupant.
    drawer.querySelectorAll('[data-addon-checkbox]').forEach(function (input) {
      if (input.checked) rememberSelection(drawer, input);
    });
  }

  /*
    The add-on behind a checked input, in the same shape variant-a publishes
    from its tiles, so the two arms compare field for field.
  */
  function cardInfo(input) {
    var card = input.closest('[data-addon-card]');
    if (!card) return null;
    var isFree = card.getAttribute('data-free-slot') === 'true';
    return {
      product: card.getAttribute('data-product-handle') || '',
      variantId: card.getAttribute('data-variant-id') || '',
      price: isFree ? 0 : Number(card.getAttribute('data-price')) || 0,
      free: isFree,
    };
  }

  var ROLE_BY_CATEGORY = {
    'classic-buckle': 'buckle',
    'spring-deployant-clasp': 'clasp',
    tools: 'tool',
    storage: 'storage',
  };

  function roleOf(input) {
    var group = input.closest('.pm__addon-products');
    var category = group ? group.getAttribute('data-category-id') || '' : '';
    return ROLE_BY_CATEGORY[category] || category;
  }

  function stepOf(input) {
    var stepEl = input.closest('[data-drawer-step]');
    return stepEl ? stepEl.getAttribute('data-drawer-step') : '';
  }

  // Step 1 is one closure slot across both of its radio groups; step 2 keeps
  // one slot per radio group, which is one per row in variant-a's terms.
  function previousFor(input) {
    if (stepOf(input) === '1') return state.previous.closure;
    return state.previous.groups[input.name] || null;
  }

  function rememberSelection(drawer, input) {
    var info = cardInfo(input);
    if (!info) return;
    if (stepOf(input) === '1') state.previous.closure = info;
    else state.previous.groups[input.name] = info;
  }

  // Same rules as variant-a, and the same function when its controller has
  // loaded on this page; the copy here is for pages where it has not.
  function changeKind(prev, next) {
    if (typeof window.wbUpsellChangeKind === 'function') return window.wbUpsellChangeKind(prev, next);
    if (!prev || prev.free) return 'first';
    if (prev.variantId === next.variantId) return 'repeat';
    if (next.price > prev.price) return 'upgrade';
    if (next.price < prev.price) return 'downgrade';
    return 'swap';
  }

  function addPayload(role, prev, next) {
    return {
      role: role,
      product: next.product,
      variant_id: next.variantId,
      price: next.price,
      free: next.free ? 'true' : 'false',
      change: changeKind(prev, next),
      replaced: prev ? prev.product : 'none',
      replaced_variant_id: prev ? prev.variantId : '',
      replaced_price: prev ? prev.price : 0,
    };
  }

  function completionPayload(drawer) {
    var closure = null;
    var extras = [];
    drawer.querySelectorAll('[data-addon-checkbox]').forEach(function (input) {
      if (!input.checked || input.disabled) return;
      var info = cardInfo(input);
      if (!info) return;
      if (stepOf(input) === '1') closure = info;
      else extras.push(info);
    });
    var bars = drawer.querySelector('[data-spring-bars-checkbox]');
    var barsChecked = !!(bars && bars.checked);
    var barsPrice = barsChecked ? Number(drawer.getAttribute('data-spring-bars-price')) || 0 : 0;

    var addonsTotal = (closure ? closure.price : 0) + barsPrice;
    var extraHandles = [];
    var paidExtras = barsChecked ? 1 : 0;
    extras.forEach(function (info) {
      addonsTotal += info.price;
      extraHandles.push(info.product || info.variantId);
      if (!info.free) paidExtras += 1;
    });
    if (barsChecked) extraHandles.push('spring-bars');

    return {
      closure: closure ? closure.product : 'none',
      closure_variant_id: closure ? closure.variantId : '',
      closure_price: closure ? closure.price : 0,
      closure_free: closure && closure.free ? 'true' : 'false',
      extras: String(paidExtras),
      extras_list: extraHandles.join('|'),
      addons_total: addonsTotal,
    };
  }

  function onDrawerHidden() {
    if (!state.open) return;
    if (!state.added && !state.completed) fire('upsell_popup_dismissed');
    state.open = false;
  }

  function watchDrawer(drawer) {
    if (!drawer || drawer.__wbSplitWatched) return;
    drawer.__wbSplitWatched = true;

    var observer = new MutationObserver(function () {
      if (isVisible(drawer)) {
        onDrawerVisible(drawer);
        scanSteps(drawer);
      } else {
        onDrawerHidden();
      }
    });
    observer.observe(drawer, {
      attributes: true,
      attributeFilter: ['style', 'aria-expanded', 'class'],
      subtree: true,
    });

    if (isVisible(drawer)) onDrawerVisible(drawer);

    // A selection is the add event for its step. Conversions dedupe per visitor,
    // so three buckles still count once, which is what the brief expects.
    drawer.addEventListener('change', function (event) {
      var input = event.target.closest && event.target.closest('[data-addon-checkbox]');
      if (!input || !input.checked) return;
      var step = stepOf(input);
      state.added = true;
      var next = cardInfo(input);
      var payload = next ? addPayload(roleOf(input), previousFor(input), next) : { role: roleOf(input) };
      if (step === '2') fire('upsell_tool_added', payload);
      else fire('upsell_buckle_added', payload);
      rememberSelection(drawer, input);
    });

    drawer.addEventListener('click', function (event) {
      var target = event.target;
      if (!target.closest) return;
      var stepAtc = target.closest('[data-action="drawer-step-atc"]');
      var goToCart = target.closest('[data-action="drawer-go-to-cart"]');
      if (!stepAtc && !goToCart) return;

      /*
        Which of the two CTAs completes the flow, decided the way control
        decides it rather than by guessing from what is on screen.

        Step 1 carries both buttons whenever step 2 has anything to offer
        (templates/product.addons.liquid:399-417): drawer-step-atc adds and
        finishes, drawer-go-to-cart moves on. Step 2 carries only the first.
        Control routes them through handleStepAtc, which always calls
        handleFinalAtc, and handleGoToCart, which only calls it when there is
        no next step.

        So drawer-step-atc is a completion wherever it is clicked. Reading the
        visible step instead missed exactly that case: a shopper who took the
        strap and skipped the tools published no completion at all, and then
        published a dismissal on the way out if they had checked nothing. The
        most ordinary way to finish was recorded as the shopper giving up.

        Read in the capture phase, because control has already moved the step
        by the time anything bubbles. Measured on the drawer: before the click
        step 1 is display:block and step 2 is none; at the drawer's own bubble
        listener step 1 is already none and step 2 is block. Judging finality
        from what is on screen at bubble time therefore called every advance a
        completion, which is the other half of this bug.
      */
      var isFinal = true;
      if (goToCart && !stepAtc) {
        var visibleSteps = Array.prototype.filter.call(
          drawer.querySelectorAll('[data-drawer-step]'),
          isVisible
        );
        var last = visibleSteps[visibleSteps.length - 1];
        isFinal =
          !last ||
          last.getAttribute('data-drawer-step') === '2' ||
          drawer.getAttribute('data-step-2-enabled') !== 'true';
      }

      // Once per open. The button is clickable again while the request is in
      // flight, and a second click was counting a second completion.
      if (isFinal && !state.completed) {
        state.completed = true;
        fire('upsell_popup_completed', completionPayload(drawer));
      }
    }, true);
  }

  /*
    Control's own failures are otherwise invisible. Its drawer is fetched and
    injected by product-addons.js, which reports nothing anywhere, so a fetch
    that 404s or a template that throws looks exactly like a shopper who was
    never offered add-ons.

    Two things are worth catching: the drawer never arriving in the mount on a
    product page that should have one, and the add to cart being cancelled by
    control's before-add handler without a drawer then becoming visible, which
    is a shopper stuck with neither popup nor cart line.
  */
  function watchForMissingDrawer(mount) {
    var sawBeforeAdd = false;

    document.addEventListener('product:before-add', function () {
      sawBeforeAdd = true;
      window.setTimeout(function () {
        // Re-checked here, not just at start. Assignments resolve after this
        // file runs, so a variant-a session looked like control at bind time and
        // then filed reports about a drawer it was never going to use.
        if (!armFor()) return;
        var drawer = mount.querySelector(DRAWER);
        if (!drawer) {
          report('control_drawer_missing', null, { note: 'before-add fired, nothing mounted' });
          return;
        }
        if (!isVisible(drawer)) {
          report('control_drawer_never_opened', null, {
            step1: drawer.getAttribute('data-step-1-enabled'),
            step2: drawer.getAttribute('data-step-2-enabled'),
          });
        }
      }, 2500);
    });

    /*
      A product page whose drawer never prefetches at all.

      Diagnostic only. It fires when the shopper has NOT pressed add to cart,
      so by construction nobody described here lost an upsell: the two events
      above are the ones that mean a shopper wanted the drawer and did not get
      it. Read this as warm-cache health, not as a failure.

      Two things kept it from meaning even that much. It fired on products with
      no add-ons, where control has nothing to prefetch and a miss is the
      correct outcome. And it judged on a fixed timer from script start, so a
      slow connection read as a missing prefetch. Of its first thirteen rows,
      ten were one product under test and twelve had no assignment resolved at
      all, which buried the single real one.

      So it now reports only where a drawer was actually expected, and only
      once the page has finished loading, the same way the animated content
      failsafe refuses to judge a page that has not settled.
    */
    function reportIfPrefetchMissing() {
      if (sawBeforeAdd) return;
      if (!armFor()) return;

      // The inline add-ons panel is control's own evidence that this product
      // has add-ons at all. No panel, nothing to prefetch, nothing to report.
      if (!document.querySelector('product-component product-addons')) return;

      if (mount.querySelector(DRAWER)) return;
      report('control_drawer_not_prefetched', null, { note: 'no drawer in the mount after load' });
    }

    function schedulePrefetchCheck() {
      window.setTimeout(reportIfPrefetchMissing, 6000);
    }

    if (document.readyState === 'complete') schedulePrefetchCheck();
    else window.addEventListener('load', schedulePrefetchCheck, { once: true });
  }

  function start() {
    if (!armFor()) return;
    var mount = document.getElementById(MOUNT_ID);
    if (!mount) {
      report('control_mount_missing', null, { note: '#addons-drawer-mount not in the layout' });
      return;
    }

    var existing = mount.querySelector(DRAWER);
    if (existing) watchDrawer(existing);

    // The drawer is injected long after this file runs, and replaced whenever a
    // different product is fetched, so the mount itself is what we watch.
    new MutationObserver(function () {
      var drawer = mount.querySelector(DRAWER);
      if (drawer) watchDrawer(drawer);
    }).observe(mount, { childList: true });

    if (document.querySelector('product-component[data-handle]')) watchForMissingDrawer(mount);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
